/**
 * Keeps each cart line's `_delivery_estimate` line-item property in sync with the
 * `data-delivery-estimate` rendered on its row.
 *
 * Why this exists
 * ----------------
 * The product form sets `_delivery_estimate` at add time, but items added by other
 * paths — upsell/addon blocks, collection quick-add, "frequently bought together" —
 * enter the cart without it. This reconciles them from the row's data attribute,
 * which is rendered server-side for that line's variant only (single_variant_only),
 * so multi-variant products get the selected variant's time, never a merged list.
 *
 * It runs on the cart page AND in the drawer, and re-runs after every cart change,
 * so an item added and checked out straight from the drawer still carries its
 * estimate. Reconciling only touches lines that are out of sync, so it converges
 * and adds no cart writes once everything matches.
 */
(function () {
  if (window.__cartDeliveryEstimateInit) return;
  window.__cartDeliveryEstimateInit = true;

  const cartUrl = () => (window.theme && window.theme.routes && window.theme.routes.cart) || '/cart';
  const changeUrl = () => (window.theme && window.theme.routes && window.theme.routes.cartChange) || '/cart/change.js';

  let running = false;
  let pending = false;
  let debounceTimer = null;

  async function reconcile() {
    // Coalesce overlapping runs; if a trigger arrives mid-run, do one more pass after.
    if (running) { pending = true; return; }
    // Nothing to do if no cart rows are in the DOM (empty cart / drawer not rendered).
    if (!document.querySelector('.cart-item[data-line-index]')) return;

    running = true;
    try {
      const res = await fetch(`${cartUrl()}.js`, { headers: { Accept: 'application/json' } });
      if (!res.ok) return;
      const cart = await res.json();
      if (!cart.items || !cart.items.length) return;

      const updates = [];
      cart.items.forEach((item, index) => {
        const line = index + 1;
        // Match the row by line + variant. querySelector returns the first match;
        // the cart page and drawer render identical data-delivery-estimate values,
        // so a duplicate row (both present on the cart page) makes no difference.
        const row = document.querySelector(
          `.cart-item[data-line-index="${line}"][data-variant-id="${item.variant_id}"]`
        );
        if (!row) return; // row not rendered yet — another trigger will catch it

        const props = item.properties || {};
        const stored = props._delivery_estimate || '';
        const estimate = row.getAttribute('data-delivery-estimate') || '';

        if (estimate === stored) return; // already in sync

        updates.push({
          line: line,
          quantity: item.quantity,
          properties: Object.assign({}, props, { _delivery_estimate: estimate })
        });
      });

      for (const update of updates) {
        await fetch(changeUrl(), {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify(update)
        });
      }
    } catch (error) {
      // Delivery estimate is non-critical — never block the cart on it.
      if (window.console && window.console.warn) window.console.warn('Delivery estimate sync failed', error);
    } finally {
      running = false;
      if (pending) { pending = false; schedule(); }
    }
  }

  function schedule() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(reconcile, 250);
  }

  // Semantic cart-change signals the theme dispatches:
  //  - on:cart:add             → product form add
  //  - dispatch:cart-drawer:refresh → upsell/addon add, quantity change, etc.
  document.addEventListener('on:cart:add', schedule);
  document.addEventListener('dispatch:cart-drawer:refresh', schedule);
  document.addEventListener('on:bfcache:load-restore', schedule);

  // Catch-all: any re-render of the drawer contents (including adds from apps that
  // don't fire the theme events). Debounced, and a no-op when already in sync.
  function observeDrawer() {
    const drawer = document.getElementById('cart-drawer');
    if (drawer) new MutationObserver(schedule).observe(drawer, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { observeDrawer(); schedule(); });
  } else {
    observeDrawer();
    schedule();
  }

  // Closing the race: reconcile before checkout submits, so an item added and
  // checked out immediately still carries its estimate. Never blocks checkout —
  // proceeds after reconcile or a 1.5s cap, whichever comes first.
  document.addEventListener(
    'submit',
    function (evt) {
      const submitter = evt.submitter || document.activeElement;
      if (!submitter || submitter.name !== 'checkout') return;
      const form = evt.target;
      if (form.dataset.deliveryReconciled === '1') return; // second pass — let it through

      evt.preventDefault();
      const proceed = function () {
        form.dataset.deliveryReconciled = '1';
        if (typeof form.requestSubmit === 'function') form.requestSubmit(submitter);
        else submitter.click();
      };
      Promise.race([
        reconcile(),
        new Promise(function (r) { setTimeout(r, 1500); })
      ]).then(proceed, proceed);
    },
    true
  );
})();
